import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Pause, PlayCircle } from "lucide-react";
import { formatDMY } from "@/lib/dateFormat";
import { toast } from "sonner";

/** Suspender SOLO el cobro: el estudiante sigue en la asistencia y consume
 *  clases, pero no se le emite la factura del ciclo siguiente. */
export default function SuspendBillingDialog({ open, onOpenChange, enrollment, studentName, onSaved }) {
  const [months, setMonths] = useState("1");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const isSuspended = !!enrollment?.suspended;

  const suspend = async () => {
    setSaving(true);
    try {
      await api.post(`/enrollments/${enrollment.id}/suspend-billing`, {
        months: months ? Number(months) : null,
        reason: reason || null,
      });
      toast.success(months ? `Cobro suspendido ${months} mes(es)` : "Cobro suspendido indefinidamente");
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo suspender el cobro");
    } finally {
      setSaving(false);
    }
  };

  const resume = async () => {
    setSaving(true);
    try {
      await api.post(`/enrollments/${enrollment.id}/resume-billing`);
      toast.success("Cobro reactivado");
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo reactivar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="suspend-billing-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pause className="h-4 w-4 text-amber-600" /> Suspender cobro
          </DialogTitle>
          <DialogDescription>
            {studentName} sigue activo en la asistencia y consume sus clases normalmente, pero
            al cerrar el ciclo <strong>no se le emite la factura del siguiente</strong>.
          </DialogDescription>
        </DialogHeader>

        {isSuspended ? (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 space-y-2" data-testid="suspend-billing-active">
            <p className="text-sm text-amber-900">
              Cobro suspendido{" "}
              {enrollment.suspended_until
                ? <>hasta el <strong>{formatDMY(enrollment.suspended_until)}</strong> (se reactiva sola)</>
                : <strong>indefinidamente</strong>}
            </p>
            {enrollment.suspend_reason && <p className="text-xs text-amber-800">Motivo: {enrollment.suspend_reason}</p>}
            {!enrollment.suspended_until && (
              <p className="text-xs text-amber-800">
                Ojo: sin plazo, cada ciclo que cierre sale sin factura hasta que la reactive.
              </p>
            )}
            <Button onClick={resume} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid="suspend-billing-resume">
              <PlayCircle className="h-4 w-4 mr-1" /> Reactivar cobro
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <Label>¿Por cuántos meses?</Label>
              <div className="flex flex-wrap gap-2 mt-1 mb-2">
                {["1", "2", "3", ""].map((v) => (
                  <Button key={v || "inf"} type="button" size="sm"
                          variant={months === v ? "default" : "outline"}
                          className={months === v ? "bg-amber-600 hover:bg-amber-700" : ""}
                          data-testid={`suspend-months-${v || "indef"}`}
                          onClick={() => setMonths(v)}>
                    {v ? `${v} mes${v === "1" ? "" : "es"}` : "Indefinido"}
                  </Button>
                ))}
              </div>
              <Input type="number" min="1" max="24" value={months} placeholder="En blanco = indefinido"
                     data-testid="suspend-months-input"
                     onChange={(e) => setMonths(e.target.value)} />
              <p className="text-xs text-slate-500 mt-2">
                Si pone un número, el cobro se reactiva solo en esa fecha. En blanco queda
                suspendido hasta que usted lo reactive a mano.
              </p>
            </div>
            <div>
              <Label>Motivo</Label>
              <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)}
                        placeholder="Se le concede un mes sin cobro…"
                        data-testid="suspend-reason-input" />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cerrar</Button>
          {!isSuspended && (
            <Button onClick={suspend} disabled={saving} className="bg-amber-600 hover:bg-amber-700"
                    data-testid="suspend-billing-confirm">
              <Pause className="h-4 w-4 mr-1" /> Suspender cobro
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
